import { jsPDF } from 'jspdf'
import { Api } from '../lib/api.js'
import { env } from '../config/env.js'

// Layout constants — A4 portrait in mm.
const PAGE_W = 210
const PAGE_H = 297
const MARGIN_X = 18
const MARGIN_TOP = 22
const MARGIN_BOTTOM = 22
const CONTENT_W = PAGE_W - MARGIN_X * 2
const FOOT_Y = PAGE_H - 12

// Colour palette pulled from tailwind.config.js so PDFs read as the same
// brand as the live UI.
const COLOR = {
  ink:    [13, 13, 13],
  muted:  [122, 122, 114],
  line:   [236, 230, 217],
  terra:  [181, 73, 31],
  fiscal: [122, 90, 12],
  actif:  [61, 90, 44],
  passif: [138, 52, 23],
}

const SECTION_TITLES = {
  bilan_personnel: 'Bilan personnel et familial',
  bilan_patrimonial: 'Bilan patrimonial',
  situation_fiscale: 'Situation fiscale',
  objectifs: 'Objectifs et projets de vie',
  strategie_investissement: "Stratégie d'investissement",
  strategie_immobiliere: 'Stratégie immobilière',
  prevoyance_protection: 'Prévoyance et protection',
  retraite: 'Préparation de la retraite',
  transmission_succession: 'Transmission et succession',
  recommandations_plan_action: "Recommandations et plan d'action",
}

const KIND_LABELS = {
  decouverte: 'Découverte',
  bilan_annuel: 'Bilan annuel',
  suivi: 'Suivi',
  recommandation: 'Recommandation',
}

// jsPDF's bundled Helvetica is WinAnsi-encoded — Unicode glyphs outside
// CP1252 (curly quotes ’, stars ★, em-dashes —, ellipses …) render as
// missing-glyph blocks. Map them to ASCII equivalents at write time so
// the PDF stays clean without needing to embed a Unicode TTF.
function safeText(s) {
  if (s == null) return ''
  return String(s)
    .replace(/[‘’‚‛]/g, "'")
    .replace(/[“”„‟]/g, '"')
    .replace(/–/g, '-')   // en-dash
    .replace(/—/g, '--')  // em-dash
    .replace(/…/g, '...') // ellipsis
    .replace(/★/g, '*')   // black star
    .replace(/•/g, '-')   // bullet (we draw our own)
}

function formatDate(iso) {
  if (!iso) return ''
  try {
    const d = new Date(iso)
    if (isNaN(d.getTime())) return ''
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
  } catch { return '' }
}

// Map each section to a brand colour pair (kicker-fg) — same conceptual
// grouping as the FactPill on-screen colour map.
const SECTION_COLOR = {
  bilan_personnel:             COLOR.ink,
  bilan_patrimonial:           COLOR.actif,
  situation_fiscale:           COLOR.fiscal,
  objectifs:                   COLOR.terra,
  strategie_investissement:    COLOR.actif,
  strategie_immobiliere:       COLOR.actif,
  prevoyance_protection:       COLOR.passif,
  retraite:                    COLOR.terra,
  transmission_succession:     COLOR.terra,
  recommandations_plan_action: COLOR.fiscal,
}

const KIND_BY_TEMPLATE = {
  cre: 'cre',
  client_summary: 'synthese-client',
  internal_notes: 'notes-internes',
}

// Human-readable label per template kind — used for the PDF's internal
// metadata Title (what shows in the browser/Acrobat title bar).
const KIND_META_LABEL = {
  cre: 'Compte-rendu',
  client_summary: 'Synthèse client',
  internal_notes: 'Notes internes',
}

// Cursor + paging helpers ------------------------------------------------

class Cursor {
  constructor(doc) {
    this.doc = doc
    this.y = MARGIN_TOP
    this.page = 1
  }
  ensure(space) {
    if (this.y + space > PAGE_H - MARGIN_BOTTOM) {
      this.doc.addPage()
      this.page += 1
      this.y = MARGIN_TOP
      drawFooter(this.doc, this.page)
    }
  }
  advance(dy) {
    this.y += dy
  }
}

// Style primitives -------------------------------------------------------

function rgb(doc, c) { doc.setTextColor(c[0], c[1], c[2]) }
function rgbDraw(doc, c) { doc.setDrawColor(c[0], c[1], c[2]) }

function setText(doc, { size, weight = 'normal', color = COLOR.ink, font = 'helvetica' }) {
  doc.setFont(font, weight)
  doc.setFontSize(size)
  rgb(doc, color)
}

// Horizontal rule full content width.
function hr(doc, y, color = COLOR.line) {
  rgbDraw(doc, color)
  doc.setLineWidth(0.2)
  doc.line(MARGIN_X, y, MARGIN_X + CONTENT_W, y)
}

// Footer drawn once per page — page number + tiny generated-by note.
function drawFooter(doc, pageNum) {
  setText(doc, { size: 8, color: COLOR.muted })
  doc.text(safeText(`Document généré par Clarity - page ${pageNum}`), MARGIN_X, FOOT_Y)
}

// Top header: kicker + title + optional subtitle.
function drawHeader(c, { kicker, title, subtitle }) {
  setText(c.doc, { size: 8, weight: 'bold', color: COLOR.terra })
  c.doc.text(safeText(kicker).toUpperCase(), MARGIN_X, c.y, { charSpace: 0.6 })
  c.advance(8)

  setText(c.doc, { size: 22, weight: 'bold' })
  c.doc.text(safeText(title), MARGIN_X, c.y)
  c.advance(8)

  if (subtitle) {
    setText(c.doc, { size: 10, color: COLOR.muted })
    c.doc.text(safeText(subtitle), MARGIN_X, c.y)
    c.advance(6)
  }
  c.advance(2)
  hr(c.doc, c.y)
  c.advance(8)
}

// Section block: kicker title + bullet list of fact values.
function drawSection(c, { title, color = COLOR.ink, items, showSource = false }) {
  if (!items || items.length === 0) return

  c.ensure(14)
  setText(c.doc, { size: 8, weight: 'bold', color })
  c.doc.text(safeText(title).toUpperCase(), MARGIN_X, c.y, { charSpace: 0.6 })
  c.advance(5.5)

  setText(c.doc, { size: 10.5, color: COLOR.ink })
  for (const f of items) {
    if (!f?.value) continue
    const lines = c.doc.splitTextToSize(safeText(f.value), CONTENT_W - 5)
    const blockH = lines.length * 5 + (showSource && f.source_text ? 4 : 0) + 1.5
    c.ensure(blockH)
    // Bullet
    c.doc.setFillColor(color[0], color[1], color[2])
    c.doc.circle(MARGIN_X + 1.4, c.y - 1.4, 0.7, 'F')
    // Value
    setText(c.doc, { size: 10.5, color: COLOR.ink })
    c.doc.text(lines, MARGIN_X + 5, c.y)
    c.advance(lines.length * 5)
    // Optional source verbatim
    if (showSource && f.source_text) {
      const srcLines = c.doc.splitTextToSize(safeText(`« ${f.source_text} »`), CONTENT_W - 5)
      setText(c.doc, { size: 8.5, weight: 'italic', color: COLOR.muted })
      c.doc.text(srcLines, MARGIN_X + 5, c.y)
      c.advance(srcLines.length * 4)
    }
    c.advance(1.5)
  }
  c.advance(4)
}

// Profile card: 4 selectors as labelled chips.
function drawProfile(c, profile, profileFields) {
  if (!profile) return
  c.ensure(20)
  setText(c.doc, { size: 8, weight: 'bold', color: COLOR.fiscal })
  c.doc.text('PROFIL CLIENT', MARGIN_X, c.y, { charSpace: 0.6 })
  c.advance(5.5)

  const civilLabel = ({ marie: 'Marié(e)', pacse: 'Pacsé(e)', celibataire: 'Célibataire', divorce: 'Divorcé(e)', veuf: 'Veuf/ve' })[profile.civilStatus] || profile.civilStatus || '—'
  const empLabel = ({ salarie: 'Salarié(e)', independant: 'Indépendant(e)', dirigeant: 'Dirigeant(e)', retraite: 'Retraité(e)', autre: 'Autre' })[profile.employment] || profile.employment || '—'
  const ownLabel = ({ proprietaire: 'Propriétaire', locataire: 'Locataire', autre: 'Autre' })[profile.ownership] || profile.ownership || '—'
  const childLabel = profile.hasChildren ? 'Avec enfants' : 'Sans enfants'

  setText(c.doc, { size: 10, color: COLOR.ink })
  const row = `${civilLabel}  ·  ${childLabel}  ·  ${empLabel}  ·  ${ownLabel}`
  c.doc.text(safeText(row), MARGIN_X, c.y)
  c.advance(7)

  // Atomic fields — render only those with a value, in two columns.
  const labels = {
    profession: 'Profession',
    regime_matrimonial: 'Régime matrimonial',
    profession_conjoint: 'Profession du conjoint',
    enfants: 'Enfants',
    etudes_enfants: 'Études des enfants',
    capacite_epargne_mensuelle: "Capacité d'épargne",
    pension_mensuelle: 'Pension mensuelle',
    residence_principale_valeur: 'Résidence principale',
    loyer_mensuel: 'Loyer mensuel',
    tmi: 'TMI',
    horizon: "Horizon d'investissement",
    profil_risque: 'Profil de risque',
  }
  const rows = []
  for (const [k, label] of Object.entries(labels)) {
    const v = (profileFields || {})[k]
    if (v && String(v).trim() !== '') rows.push([label, String(v).trim()])
  }
  if (rows.length === 0) return
  const colW = CONTENT_W / 2
  for (let i = 0; i < rows.length; i += 2) {
    c.ensure(8)
    const left = rows[i]
    const right = rows[i + 1]
    setText(c.doc, { size: 8.5, color: COLOR.muted })
    c.doc.text(safeText(left[0]), MARGIN_X, c.y)
    if (right) c.doc.text(safeText(right[0]), MARGIN_X + colW, c.y)
    setText(c.doc, { size: 10, color: COLOR.ink })
    c.doc.text(c.doc.splitTextToSize(safeText(left[1]), colW - 4), MARGIN_X, c.y + 4.5)
    if (right) c.doc.text(c.doc.splitTextToSize(safeText(right[1]), colW - 4), MARGIN_X + colW, c.y + 4.5)
    c.advance(11)
  }
  c.advance(4)
  hr(c.doc, c.y)
  c.advance(6)
}

// Renderers per template kind --------------------------------------------

// Title-precedence helper shared by all three renderers:
//   1. client name when the session is linked to a client row
//   2. otherwise kind label + session date
//   3. otherwise a static fallback
// Subtitle complements the title with the bits not used in it.
function deriveHeader(props, fallbackTitle) {
  const date = formatDate(props.startedAt)
  const kindLabel = KIND_LABELS[props.kind] || ''
  const ref = (props.sessionId || '').slice(0, 8)
  if (props.clientName) {
    const subtitleBits = [kindLabel, date, ref ? `Réf : ${ref}` : null].filter(Boolean)
    return { title: props.clientName, subtitle: subtitleBits.join('  ·  ') }
  }
  if (kindLabel || date) {
    const subtitleBits = [ref ? `Réf : ${ref}` : null].filter(Boolean)
    return {
      title: [kindLabel, date].filter(Boolean).join('  ·  '),
      subtitle: subtitleBits.join('  ·  '),
    }
  }
  return { title: fallbackTitle, subtitle: ref ? `Réf : ${ref}` : '' }
}

function renderCRE(doc, props) {
  const c = new Cursor(doc)
  drawFooter(doc, 1)
  const { title, subtitle } = deriveHeader(props, 'Compte-rendu de séance')
  drawHeader(c, { kicker: "Compte-rendu d'entretien", title, subtitle })
  drawProfile(c, props.profile, props.profileFields)
  for (const [key, secTitle] of Object.entries(SECTION_TITLES)) {
    const items = props.facts?.[key] || []
    drawSection(c, { title: secTitle, color: SECTION_COLOR[key] || COLOR.ink, items, showSource: false })
  }
}

function renderClientSummary(doc, props) {
  const c = new Cursor(doc)
  drawFooter(doc, 1)
  const { title, subtitle } = deriveHeader(props, 'Synthèse de séance')
  drawHeader(c, { kicker: "Synthèse de l'entretien", title, subtitle })

  // Essentials only across the four core sections.
  const ESSENTIEL_KEYS = ['bilan_personnel', 'situation_fiscale', 'bilan_patrimonial', 'strategie_investissement']
  const essentials = ESSENTIEL_KEYS.flatMap((k) =>
    (props.facts?.[k] || []).filter((f) => f.is_essentiel).map((f) => ({ ...f, _section: k })),
  )
  if (essentials.length > 0) {
    c.ensure(14)
    setText(c.doc, { size: 8, weight: 'bold', color: COLOR.fiscal })
    c.doc.text('LE CONTEXTE ESSENTIEL', MARGIN_X, c.y, { charSpace: 0.6 })
    c.advance(6)
    for (const f of essentials) {
      const text = safeText(f.value)
      const lines = c.doc.splitTextToSize(text, CONTENT_W - 6)
      const blockH = lines.length * 5 + 1
      c.ensure(blockH)
      // Star bullet — drawn as a filled diamond/circle since the WinAnsi
      // font doesn't include U+2605. Section colour for visual grouping.
      const col = SECTION_COLOR[f._section] || COLOR.fiscal
      c.doc.setFillColor(col[0], col[1], col[2])
      c.doc.circle(MARGIN_X + 1.4, c.y - 1.4, 1.0, 'F')
      setText(c.doc, { size: 11, color: COLOR.ink })
      c.doc.text(lines, MARGIN_X + 6, c.y)
      c.advance(blockH)
    }
    c.advance(4)
  }

  drawSection(c, {
    title: "Plan d'action proposé",
    color: COLOR.terra,
    items: props.facts?.recommandations_plan_action,
  })

  drawSection(c, { title: 'Objectifs', color: COLOR.terra, items: props.facts?.objectifs })

  c.advance(8)
  hr(c.doc, c.y)
  c.advance(5)
  setText(c.doc, { size: 8.5, color: COLOR.muted })
  c.doc.text(safeText(`Document de synthèse - non contractuel. Référence : ${props.sessionId || ''}.`), MARGIN_X, c.y)
}

function renderInternalNotes(doc, props) {
  const c = new Cursor(doc)
  drawFooter(doc, 1)
  const { title, subtitle } = deriveHeader(props, 'Notes de séance')
  drawHeader(c, { kicker: 'Notes internes', title, subtitle })
  drawProfile(c, props.profile, props.profileFields)
  for (const [key, secTitle] of Object.entries(SECTION_TITLES)) {
    const items = props.facts?.[key] || []
    drawSection(c, { title: secTitle, color: SECTION_COLOR[key] || COLOR.ink, items, showSource: true })
  }
}

const RENDERERS = {
  cre: renderCRE,
  client_summary: renderClientSummary,
  internal_notes: renderInternalNotes,
}

// Public entry point -----------------------------------------------------

/**
 * Generates a PDF for the given template kind and props, triggers a local
 * download, and (when token + sessionId provided) fires a best-effort
 * archive POST. Pure-jsPDF render — no DOM snapshot, so layout is
 * deterministic and content is always present.
 *
 * Props shape: { sessionId, profile, profileFields, facts, token, onArchived }.
 */
export async function renderExportPdf(kind, props) {
  const render = RENDERERS[kind]
  if (!render) throw new Error(`unknown export kind: ${kind}`)

  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' })
  // Helvetica is bundled with jsPDF and renders crisply at all sizes;
  // Inter would require embedding a TTF (~150 KB). Keeping Helvetica for
  // now — the layout-level brand identity (kicker tracking, terra accent,
  // colored bullets) matches the app well enough.
  doc.setFont('helvetica', 'normal')

  // PDF metadata — surfaces as the title in browser tabs / Acrobat / Finder
  // Quick Look. Without these, viewers fall back to the filename.
  const metaLabel = KIND_META_LABEL[kind] || 'Document'
  const metaTail = props.clientName || formatDate(props.startedAt) || (props.sessionId || '').slice(0, 8)
  doc.setProperties({
    title: metaTail ? `${metaLabel} — ${metaTail}` : metaLabel,
    subject: metaLabel,
    creator: 'Clarity',
    author: 'Clarity',
  })

  render(doc, props)

  const blob = doc.output('blob')
  const filename = `${KIND_BY_TEMPLATE[kind]}-${(props.sessionId || 'session').slice(0, 8)}.pdf`

  // Type as octet-stream so Chrome / Edge can't open in their inline PDF
  // viewer — the <a download> attribute then takes effect and the user
  // gets the proper filename.
  const downloadBlob = new Blob([blob], { type: 'application/octet-stream' })
  const url = URL.createObjectURL(downloadBlob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)

  // Best-effort archive — never throws.
  if (props.sessionId && props.token) {
    try {
      const api = new Api({ baseURL: env.API_BASE_URL || '', getToken: () => props.token })
      const archived = await api.archiveExport(props.sessionId, kind, blob)
      props.onArchived?.(archived)
    } catch {
      // Swallow — the local download is the user's primary artifact.
    }
  }
}
